import { useState, useEffect } from 'react';
import { Stack, Button, TextInput, Textarea, Select, Group, Text, Paper, Loader, Checkbox, Box, Collapse } from '@mantine/core';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import MilkdownRenderer from '../shared/MilkdownRenderer';
import { useResumeContentItem, useCreateResumeContent, useUpdateResumeContent } from '../../api/resumes';
import { useResumeStore, EditingType } from '../../store/resume.store';

interface ResumeContentEditorProps {
  contentId?: string;
  editingType: EditingType;
  onClose: () => void;
}

export default function ResumeContentEditor({
  contentId,
  editingType,
  onClose,
}: ResumeContentEditorProps) {
  const [previewVisible, setPreviewVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: 'skills' as const,
    content_markdown: '',
    description: '',
    is_default: false,
    include_by_default: false,
  });

  // Always call hooks at the top level - don't conditionally call them
  const { data: existingContent, isLoading } = useResumeContentItem(
    editingType === 'edit' ? contentId : undefined
  );
  const createMutation = useCreateResumeContent();
  // Always call this hook, even if contentId is undefined - the hook itself handles it
  const updateMutation = useUpdateResumeContent(contentId || '');

  // Load existing content into form
  useEffect(() => {
    if (editingType === 'edit' && existingContent) {
      setFormData({
        name: existingContent.name,
        type: existingContent.type as any,
        content_markdown: existingContent.content_markdown,
        description: existingContent.description || '',
        is_default: existingContent.is_default || false,
        include_by_default: existingContent.include_by_default || false,
      });
    }
  }, [editingType, existingContent]);

  const handleSave = async () => {
    try {
      const payload = {
        name: formData.name,
        type: formData.type,
        content_markdown: formData.content_markdown,
        description: formData.description || undefined,
        is_default: formData.is_default,
        include_by_default: formData.include_by_default,
      };

      console.log('Saving content with payload:', payload);

      if (editingType === 'create') {
        await createMutation.mutateAsync(payload);
      } else if (updateMutation) {
        await updateMutation.mutateAsync(payload);
      }
      onClose();
    } catch (error) {
      console.error('Failed to save content:', error);
      if (error instanceof Error) {
        console.error('Error message:', error.message);
        console.error('Error details:', error);
      }
    }
  };

  const isLoading_ = isLoading || createMutation.isPending || updateMutation?.isPending;
  const isValid = formData.name && formData.content_markdown;

  return (
    <Stack gap="md">
      {/* Form Fields - Full Width */}
      <TextInput
        label="Content Name"
        placeholder="e.g., Master Template v1, Python Skills"
        value={formData.name}
        onChange={(e) => setFormData((prev) => ({ ...prev, name: e.currentTarget.value }))}
        disabled={isLoading_}
      />

      <Select
        label="Content Type"
        data={[
          { value: 'masterTemplate', label: 'Master Template' },
          { value: 'skills', label: 'Skills' },
          { value: 'experience', label: 'Experience' },
          { value: 'fullResume', label: 'Full Resume' },
          { value: 'education', label: 'Education' },
          { value: 'notes', label: 'Notes' },
        ]}
        value={formData.type}
        onChange={(value) =>
          setFormData((prev) => ({ ...prev, type: value as any }))
        }
        disabled={isLoading_}
      />

      <Textarea
        label="Description (Optional)"
        placeholder="Brief description of this content"
        value={formData.description}
        onChange={(e) => setFormData((prev) => ({ ...prev, description: e.currentTarget.value }))}
        rows={2}
        disabled={isLoading_}
      />

      {/* Default Selection Options */}
      <Box>
        {(formData.type as string) === 'masterTemplate' && (
          <Checkbox
            label="Set as default master template"
            description="Only one master template can be default"
            checked={formData.is_default}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, is_default: e.currentTarget.checked }))
            }
            disabled={isLoading_}
          />
        )}
        {(formData.type as string) !== 'masterTemplate' && (
          <Checkbox
            label="Include by default"
            description="Auto-check this content when creating new resumes"
            checked={formData.include_by_default}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, include_by_default: e.currentTarget.checked }))
            }
            disabled={isLoading_}
          />
        )}
      </Box>

      <div>
        <Text fw={500} mb="xs">
          Content (Markdown)
        </Text>
        <Textarea
          placeholder="Enter your resume content in markdown format..."
          value={formData.content_markdown}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, content_markdown: e.currentTarget.value }))
          }
          rows={10}
          minRows={10}
          disabled={isLoading_}
          styles={{
            input: {
              fontFamily: 'monospace',
              fontSize: '0.875rem',
            },
          }}
        />
      </div>

      {/* Buttons */}
      <Group justify="flex-end" gap="xs">
        <Button variant="light" onClick={onClose} disabled={isLoading_}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={!isValid || isLoading_}
          loading={isLoading_}
        >
          {editingType === 'create' ? 'Create' : 'Update'}
        </Button>
      </Group>

      {/* Collapsible Preview - Full Width Below Buttons */}
      <div>
        <Button
          variant="subtle"
          onClick={() => setPreviewVisible(!previewVisible)}
          rightSection={previewVisible ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
          fullWidth
        >
          {previewVisible ? 'Hide Preview' : 'Show Preview'}
        </Button>
        <Collapse in={previewVisible}>
          <Paper p="md" radius="md" bg="gray.0" withBorder style={{ marginTop: '12px' }}>
            {isLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                <Loader />
              </div>
            ) : (
              <MilkdownRenderer
                content={formData.content_markdown || '*(Your markdown preview will appear here)*'}
              />
            )}
          </Paper>
        </Collapse>
      </div>
    </Stack>
  );
}
